import { Fragment, useId } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { OptionRow, OptionsGroup, RadioCircle } from "@acto/taskpane/field-sales-2/components/primitives/SheetOptions";
import { LabeledTextArea } from "@acto/taskpane/field-sales-2/components/primitives/LabeledTextArea";

export type ReasonKind = "notSuitable" | "dataIncorrect";

export interface ReasonDraft {
  kind: ReasonKind;
  text: string;
}

const REASONS: readonly ReasonKind[] = ["notSuitable", "dataIncorrect"];

const List = styled(OptionsGroup)(({ theme }) => ({
  paddingInline: 0,
  marginTop: 0,
  gap: theme.fieldSalesTwo.spacing[10],
}));

const InputCard = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius["32"],
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderBright}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

const Hint = styled("p")(({ theme }) => ({
  margin: 0,
  paddingInline: theme.fieldSalesTwo.spacing[20],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight["400"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

interface ProductHideReasonPickerProps {
  value: ReasonDraft | null;
  onChange: (value: ReasonDraft) => void;
}

export function ProductHideReasonPicker({ value, onChange }: ProductHideReasonPickerProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const id = useId();

  const reasonLabel = (kind: ReasonKind) =>
    kind === "dataIncorrect" ? t("reasonDataIncorrect") : t("reasonNotSuitable");

  const questionLabel = (kind: ReasonKind) =>
    kind === "dataIncorrect" ? t("reasonInputLabelDataIncorrect") : t("reasonInputLabelNotSuitable");

  return (
    <List role="group" aria-label={t("overlaySubtitle")}>
      {REASONS.map((kind) =>
        kind === value?.kind ? (
          <Fragment key={kind}>
            <InputCard>
              <LabeledTextArea
                id={`${id}-${kind}`}
                label={questionLabel(kind)}
                autoFocus
                aria-describedby={kind === "dataIncorrect" ? `${id}-${kind}-hint` : undefined}
                value={value.text}
                onChange={(event) => onChange({ kind, text: event.target.value })}
              />
            </InputCard>
            {kind === "dataIncorrect" && <Hint id={`${id}-${kind}-hint`}>{t("reasonHintDataIncorrect")}</Hint>}
          </Fragment>
        ) : (
          <OptionRow key={kind} type="button" onClick={() => onChange({ kind, text: "" })}>
            {reasonLabel(kind)}
            <RadioCircle aria-hidden="true" />
          </OptionRow>
        ),
      )}
    </List>
  );
}
